/**
 * SchoolMessages — Parent-Teacher Messaging Admin View
 */
import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import {
  MessageSquare, Users, Inbox, Send, Search, X, Clock,
  CheckCircle2, Loader2, MessageCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card><CardContent className="p-4"><div className="flex items-center gap-3">
    <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center`}><Icon className={`h-5 w-5 text-${color}-600`} /></div>
    <div><p className="text-xl font-bold text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}</p></div>
  </div></CardContent></Card>
);

const timeAgo = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

export const SchoolMessages = ({ tenantId }) => {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedConvo, setSelectedConvo] = useState(null);
  const messagesEndRef = useRef(null);

  const { data: dashboard } = useQuery({
    queryKey: ['messages-dashboard', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/messages/dashboard`),
  });

  const { data: conversations = [] } = useQuery({
    queryKey: ['messages-conversations', tenantId, search],
    queryFn: () => api(`/tenants/${tenantId}/messages/conversations${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  });

  const { data: convoDetail, refetch: refetchDetail } = useQuery({
    queryKey: ['messages-detail', selectedConvo],
    queryFn: () => api(`/tenants/${tenantId}/messages/conversations/${selectedConvo}`),
    enabled: !!selectedConvo,
  });

  const closeMutation = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/portal/messages/${id}/close`, { method: 'PUT' }),
    onSuccess: () => {
      toast.success('Conversation closed');
      qc.invalidateQueries({ queryKey: ['messages-conversations'] });
      qc.invalidateQueries({ queryKey: ['messages-dashboard'] });
    },
  });

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [convoDetail?.messages]);

  const filtered = conversations;

  return (
    <div className="space-y-5" data-testid="school-messages">
      <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
        <MessageSquare className="h-5 w-5 text-blue-600" /> Parent-Teacher Messages
      </h2>

      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="messages-dashboard">
          <StatCard label="Conversations" value={dashboard.total_conversations} icon={MessageCircle} color="blue" />
          <StatCard label="Messages" value={dashboard.total_messages} icon={Inbox} color="green" />
          <StatCard label="Active" value={dashboard.active_conversations} icon={CheckCircle2} color="indigo" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ minHeight: 400 }}>
        {/* Conversation List */}
        <div className="lg:col-span-1 border rounded-lg overflow-hidden">
          <div className="p-3 border-b bg-slate-50">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
              <Input placeholder="Search conversations..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="messages-search" />
            </div>
          </div>
          <div className="divide-y max-h-[500px] overflow-y-auto">
            {filtered.length === 0 && (
              <div className="p-8 text-center text-slate-400 text-sm">No conversations yet</div>
            )}
            {filtered.map(c => (
              <div
                key={c.id}
                className={`p-3 cursor-pointer hover:bg-blue-50 transition-colors ${selectedConvo === c.id ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`}
                onClick={() => setSelectedConvo(c.id)}
                data-testid={`convo-item-${c.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{c.parent_name} & {c.teacher_name}</p>
                    <p className="text-xs text-slate-500 truncate">{c.subject}</p>
                    {c.last_message_preview && (
                      <p className="text-xs text-slate-400 truncate mt-0.5">{c.last_message_preview}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    <p className="text-[10px] text-slate-400">{timeAgo(c.last_message_at)}</p>
                    <Badge className={`text-[10px] mt-0.5 ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {c.status}
                    </Badge>
                  </div>
                </div>
                <p className="text-[10px] text-slate-300 mt-1">{c.message_count || 0} messages</p>
              </div>
            ))}
          </div>
        </div>

        {/* Message Thread */}
        <div className="lg:col-span-2 border rounded-lg flex flex-col">
          {!selectedConvo ? (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageSquare className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Select a conversation to view messages</p>
              </div>
            </div>
          ) : convoDetail ? (
            <>
              {/* Header */}
              <div className="p-3 border-b bg-slate-50 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{convoDetail.conversation.subject}</p>
                  <p className="text-xs text-slate-500">
                    {convoDetail.conversation.parent_name} (Parent) — {convoDetail.conversation.teacher_name} (Teacher)
                  </p>
                </div>
                <div className="flex gap-2">
                  {convoDetail.conversation.status === 'active' && (
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => closeMutation.mutate(selectedConvo)} data-testid="close-convo-btn">
                      <X className="h-3 w-3 mr-1" /> Close
                    </Button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[400px]" data-testid="message-thread">
                {(convoDetail.messages || []).map((msg, i) => (
                  <div key={msg.id || i} className={`flex ${msg.sender_type === 'teacher' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] rounded-lg px-3 py-2 ${
                      msg.sender_type === 'teacher' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-800'
                    }`}>
                      <p className="text-xs font-medium opacity-80 mb-0.5">{msg.sender_name} ({msg.sender_type})</p>
                      <p className="text-sm">{msg.content}</p>
                      <p className={`text-[10px] mt-1 ${msg.sender_type === 'teacher' ? 'text-blue-200' : 'text-slate-400'}`}>
                        {new Date(msg.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
                {(convoDetail.messages || []).length === 0 && (
                  <p className="text-center text-sm text-slate-400 py-8">No messages in this conversation yet</p>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Admin note */}
              <div className="p-3 border-t bg-slate-50">
                <p className="text-xs text-slate-400 text-center">Admin view — Messages are sent by parents and teachers through the school portal</p>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchoolMessages;
